import { CardFieldKey, CardFieldKeys, PatchCardDto, Shop } from "@/types/v2";

/** The legacy single-line card field slots, in display order. */
const LEGACY_CARD_FIELD_KEYS: CardFieldKeys[] = [
  "primaryField",
  "secondaryField",
  "tertiaryField",
];

// Migration mapping based on CARD_FIELD_OPTIONS to enhanced fields
export const FIELD_MIGRATION_MAP: Record<
  CardFieldKey,
  {
    target: string | string[];
    type:
      | "passthrough"
      | "text"
      | "name_parsing"
      | "vehicle_parsing"
      | "partial_vehicle_parsing"
      | "split_parsing"
      | "phone_parsing";
    fields?: string[];
  }
> = {
  "RO Number": {
    target: "cardNumber",
    type: "passthrough",
  },
  "Customer Last Name": {
    target: "customer.lastName",
    type: "text",
  },
  "Customer Name": {
    target: "customer.firstName",
    type: "name_parsing", // May need to parse "John Doe" -> firstName: "John", lastName: "Doe"
  },
  "Key Tag": {
    target: "keyTag",
    type: "passthrough",
  },
  "Year, Make, Model of vehicle": {
    target: "vehicle",
    type: "vehicle_parsing", // Parse "2020 Toyota Camry" -> year: 2020, make: "Toyota", model: "Camry"
    fields: ["year", "make", "model"],
  },
  "YY Model": {
    target: "vehicle",
    type: "partial_vehicle_parsing", // Parse "20 Camry" -> year: 2020, model: "Camry"
    fields: ["year", "model"],
  },
  "RO Number - Tag": {
    target: ["cardNumber", "keyTag"],
    type: "split_parsing", // Parse "RO-123 - TAG-456" -> cardNumber: "RO-123", keyTag: "TAG-456"
  },
  "Phone Number": {
    target: "customer.phone[0].number",
    type: "phone_parsing", // Parse and format phone number, default to "mobile" type
  },
  Custom: {
    target: "",
    type: "passthrough", // Custom fields are user-defined and don't migrate
  },
};

// Parsing utility functions
export const parseVehicleInfo = (
  vehicleString: string
): { year?: number; make?: string; model?: string } => {
  if (!vehicleString || typeof vehicleString !== "string") {
    return {};
  }

  const trimmed = vehicleString.trim();
  if (!trimmed) {
    return {};
  }

  // Split by various potential separators
  const parts = trimmed.split(/[,\s-]+/).filter((part) => part.length > 0);

  if (parts.length === 0) {
    return {};
  }

  const result: { year?: number; make?: string; model?: string } = {};

  // Try to find year (4-digit number or 2-digit number that makes sense)
  const yearIndex = parts.findIndex((part) => {
    const num = parseInt(part, 10);
    // 4-digit year between 1900 and current year + 2
    if (
      part.length === 4 &&
      num >= 1900 &&
      num <= new Date().getFullYear() + 2
    ) {
      return true;
    }
    // 2-digit year that could be converted (assume 00-30 is 2000s, 31-99 is 1900s)
    if (part.length === 2 && !isNaN(num)) {
      return true;
    }
    return false;
  });

  if (yearIndex !== -1) {
    const yearStr = parts[yearIndex];
    let year = parseInt(yearStr, 10);

    if (yearStr.length === 2) {
      // Convert 2-digit to 4-digit year
      if (year <= 30) {
        year += 2000; // 00-30 -> 2000-2030
      } else {
        year += 1900; // 31-99 -> 1931-1999
      }
    }

    result.year = year;

    // Remaining parts after year are make and model
    const remainingParts = parts.filter((_, index) => index !== yearIndex);
    if (remainingParts.length >= 1) {
      result.make = remainingParts[0];
    }
    if (remainingParts.length >= 2) {
      result.model = remainingParts.slice(1).join(" ");
    }
  } else {
    // No year found, assume all parts are make and model
    if (parts.length >= 1) {
      result.make = parts[0];
    }
    if (parts.length >= 2) {
      result.model = parts.slice(1).join(" ");
    }
  }

  return result;
};

export const parsePartialVehicleInfo = (
  vehicleString: string
): { year?: number; model?: string } => {
  if (!vehicleString || typeof vehicleString !== "string") {
    return {};
  }

  const trimmed = vehicleString.trim();
  if (!trimmed) {
    return {};
  }

  // Split by various potential separators
  const parts = trimmed.split(/[,\s-]+/).filter((part) => part.length > 0);

  if (parts.length === 0) {
    return {};
  }

  const result: { year?: number; model?: string } = {};

  // Look for year in first part (likely 2-digit)
  const firstPart = parts[0];
  const num = parseInt(firstPart, 10);

  if (!isNaN(num)) {
    let year = num;
    if (firstPart.length === 2) {
      // Convert 2-digit to 4-digit year
      if (year <= 30) {
        year += 2000; // 00-30 -> 2000-2030
      } else {
        year += 1900; // 31-99 -> 1931-1999
      }
    }

    if (year >= 1900 && year <= new Date().getFullYear() + 2) {
      result.year = year;
      // Remaining parts are model
      if (parts.length >= 2) {
        result.model = parts.slice(1).join(" ");
      }
    } else {
      // First part doesn't look like a year, assume all parts are model
      result.model = parts.join(" ");
    }
  } else {
    // First part isn't a number, assume all parts are model
    result.model = parts.join(" ");
  }

  return result;
};

export const parseCustomerName = (
  nameString: string
): { firstName?: string; lastName?: string } => {
  if (!nameString || typeof nameString !== "string") {
    return {};
  }

  const trimmed = nameString.trim();
  if (!trimmed) {
    return {};
  }

  // Split by spaces and filter out empty parts
  const parts = trimmed.split(/\s+/).filter((part) => part.length > 0);

  if (parts.length === 0) {
    return {};
  }

  if (parts.length === 1) {
    // Single name, put it in firstName
    return { firstName: parts[0] };
  }

  // Multiple parts - first is firstName, rest is lastName
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

export const parseSplitField = (
  splitString: string
): { cardNumber?: string; keyTag?: string } => {
  if (!splitString || typeof splitString !== "string") {
    return {};
  }

  const trimmed = splitString.trim();
  if (!trimmed) {
    return {};
  }

  // Try various separators to split the string
  const separators = [
    " - ",
    " -",
    "- ",
    "-",
    " | ",
    " |",
    "| ",
    "|",
    " / ",
    " /",
    "/ ",
    "/",
    "  ",
    " ",
  ];

  for (const separator of separators) {
    if (trimmed.includes(separator)) {
      const parts = trimmed
        .split(separator)
        .filter((part) => part.trim().length > 0);
      if (parts.length >= 2) {
        return {
          cardNumber: parts[0].trim(),
          keyTag: parts[1].trim(),
        };
      }
    }
  }

  // No separator found, put everything in cardNumber
  return { cardNumber: trimmed };
};

export const parsePhoneNumber = (
  phoneString: string
): { number: string; type: string; primary: boolean } | null => {
  if (!phoneString || typeof phoneString !== "string") {
    return null;
  }

  const trimmed = phoneString.trim();
  if (!trimmed) {
    return null;
  }

  // Extract digits only
  const digits = trimmed.replace(/\D/g, "");

  // Must have at least 10 digits for a valid phone number
  if (digits.length < 10) {
    return null;
  }

  let phoneDigits: string;
  let formatted: string;

  if (digits.length === 11 && digits.startsWith("1")) {
    // US phone number with country code 1
    phoneDigits = digits.slice(1, 11); // Remove country code
    formatted = `+1 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 10)}`;
  } else {
    // Take the first 10 digits (US phone number format)
    phoneDigits = digits.slice(0, 10);
    formatted = `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 10)}`;
  }

  return {
    number: formatted,
    type: "mobile", // Default to mobile
    primary: true,
  };
};

// Type for the migration result
type MigrationData = Partial<{
  cardNumber: string;
  keyTag: string;
  customer: Partial<{
    firstName: string;
    lastName: string;
    phone: Array<{ number: string; type: string; primary: boolean }>;
  }>;
  vehicle: Partial<{
    year: number;
    make: string;
    model: string;
  }>;
  [key: string]: unknown; // For any additional fields that might be migrated
}>;

// Main migration function that takes card data and shop configuration and returns migrated values
export const migrateCardFieldData = (
  cardData: PatchCardDto,
  shopConfig: Shop
): MigrationData => {
  const migrationData: MigrationData = {};

  if (!cardData || !shopConfig?.cardConfig?.cardContents) {
    return migrationData;
  }

  const cardContents = shopConfig.cardConfig.cardContents;

  // Process each configured field
  LEGACY_CARD_FIELD_KEYS.forEach((fieldKey) => {
    const fieldValue = cardData[fieldKey];
    const fieldOption = cardContents[fieldKey];

    if (
      !fieldValue ||
      !fieldOption ||
      // @ts-ignore
      fieldOption === "" ||
      !FIELD_MIGRATION_MAP[fieldOption as CardFieldKey]
    ) {
      return;
    }

    const migration = FIELD_MIGRATION_MAP[fieldOption as CardFieldKey];

    try {
      switch (migration.type) {
        case "passthrough":
          // Direct mapping - value goes to target field as-is
          if (typeof migration.target === "string") {
            setNestedValue(migrationData, migration.target, fieldValue);
          }
          break;

        case "text":
          // Simple text mapping
          if (typeof migration.target === "string") {
            setNestedValue(migrationData, migration.target, fieldValue);
          }
          break;

        case "name_parsing":
          // Parse customer name into firstName and lastName
          const nameData = parseCustomerName(fieldValue);
          if (nameData.firstName) {
            setNestedValue(
              migrationData,
              "customer.firstName",
              nameData.firstName
            );
          }
          if (nameData.lastName) {
            setNestedValue(
              migrationData,
              "customer.lastName",
              nameData.lastName
            );
          }
          break;

        case "vehicle_parsing":
          // Parse full vehicle info
          const vehicleData = parseVehicleInfo(fieldValue);
          if (vehicleData.year) {
            setNestedValue(migrationData, "vehicle.year", vehicleData.year);
          }
          if (vehicleData.make) {
            setNestedValue(migrationData, "vehicle.make", vehicleData.make);
          }
          if (vehicleData.model) {
            setNestedValue(migrationData, "vehicle.model", vehicleData.model);
          }
          break;

        case "partial_vehicle_parsing":
          // Parse partial vehicle info (year and model only)
          const partialVehicleData = parsePartialVehicleInfo(fieldValue);
          if (partialVehicleData.year) {
            setNestedValue(
              migrationData,
              "vehicle.year",
              partialVehicleData.year
            );
          }
          if (partialVehicleData.model) {
            setNestedValue(
              migrationData,
              "vehicle.model",
              partialVehicleData.model
            );
          }
          break;

        case "split_parsing":
          // Split field into multiple targets
          const splitData = parseSplitField(fieldValue);
          if (splitData.cardNumber) {
            setNestedValue(migrationData, "cardNumber", splitData.cardNumber);
          }
          if (splitData.keyTag) {
            setNestedValue(migrationData, "keyTag", splitData.keyTag);
          }
          break;

        case "phone_parsing":
          // Parse phone number
          const phoneData = parsePhoneNumber(fieldValue);
          if (phoneData) {
            setNestedValue(migrationData, "customer.phone", [phoneData]);
          }
          break;
      }
    } catch (error) {
      console.warn(
        `Failed to migrate field ${fieldKey} with value "${fieldValue}":`,
        error
      );
      // Fallback: keep original field value accessible
      migrationData[`_originalField_${fieldKey}`] = fieldValue;
    }
  });

  return migrationData;
};

// Helper function to set nested values (e.g., "customer.firstName" -> obj.customer.firstName)
function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void {
  const keys = path.split(".");
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (
      !(key in current) ||
      typeof current[key] !== "object" ||
      current[key] === null
    ) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  const finalKey = keys[keys.length - 1];
  current[finalKey] = value;
}

// Helper function to get nested values
export function getNestedValue(
  obj: Record<string, unknown>,
  path: string
): unknown {
  const keys = path.split(".");
  let current: unknown = obj;

  for (const key of keys) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== "object"
    ) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return current;
}

// Helper function to check if a value should be considered "empty" for migration purposes
function isEmptyValue(value: any): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (typeof value === "number" && (isNaN(value) || !isFinite(value)))
    return false; // Numbers are never "empty" if valid
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === "object" && value !== null) {
    // For objects, check if all values are empty
    const keys = Object.keys(value);
    if (keys.length === 0) return true;
    return keys.every((key) => isEmptyValue(value[key]));
  }
  return false;
}

// Function to determine which legacy fields should be hidden based on successful migration
export const determineHiddenLegacyFields = (
  migrationData: MigrationData,
  cardData: PatchCardDto,
  shopConfig: Shop
): Set<CardFieldKeys> => {
  const hiddenFields = new Set<CardFieldKeys>();

  if (!cardData || !shopConfig?.cardConfig?.cardContents || !migrationData) {
    return hiddenFields;
  }

  const cardContents = shopConfig.cardConfig.cardContents;

  // Check each configured legacy field to see if it was successfully migrated
  LEGACY_CARD_FIELD_KEYS.forEach((fieldKey) => {
    const fieldValue = cardData[fieldKey];
    const fieldOption = cardContents[fieldKey];

    // A "Custom" slot has nowhere to migrate to, so its visibility is driven by
    // the shop's Card Layout config alone. Keep it rendered even when blank so
    // it can still be filled in after the card was created.
    if (fieldOption === "Custom") {
      return;
    }

    if (
      !fieldValue ||
      !fieldOption ||
      // @ts-ignore
      fieldOption === "" ||
      !FIELD_MIGRATION_MAP[fieldOption as CardFieldKey]
    ) {
      // No field configured or no value to migrate, hide it
      hiddenFields.add(fieldKey);
      return;
    }

    const migration = FIELD_MIGRATION_MAP[fieldOption as CardFieldKey];

    // Check if migration successfully populated enhanced fields
    let migrationSuccessful = false;

    switch (migration.type) {
      case "passthrough":
        // Passthrough fields should be hidden if they have a valid target and
        // value was migrated. ("Custom" already returned above.)
        if (typeof migration.target === "string") {
          const migratedValue = getNestedValue(migrationData, migration.target);
          migrationSuccessful = !isEmptyValue(migratedValue);
        }
        break;
      case "text":
        if (typeof migration.target === "string") {
          const migratedValue = getNestedValue(migrationData, migration.target);
          migrationSuccessful = !isEmptyValue(migratedValue);
        }
        break;

      case "name_parsing":
        // Customer name parsing - check if either firstName or lastName was populated
        const firstName = getNestedValue(migrationData, "customer.firstName");
        const lastName = getNestedValue(migrationData, "customer.lastName");
        migrationSuccessful =
          !isEmptyValue(firstName) || !isEmptyValue(lastName);
        break;

      case "vehicle_parsing":
        // Full vehicle parsing - check if any vehicle field was populated
        const year = getNestedValue(migrationData, "vehicle.year");
        const make = getNestedValue(migrationData, "vehicle.make");
        const model = getNestedValue(migrationData, "vehicle.model");
        migrationSuccessful =
          !isEmptyValue(year) || !isEmptyValue(make) || !isEmptyValue(model);
        break;

      case "partial_vehicle_parsing":
        // Partial vehicle parsing - check if year or model was populated
        const partialYear = getNestedValue(migrationData, "vehicle.year");
        const partialModel = getNestedValue(migrationData, "vehicle.model");
        migrationSuccessful =
          !isEmptyValue(partialYear) || !isEmptyValue(partialModel);
        break;

      case "split_parsing":
        // Split parsing - check if either cardNumber or keyTag was populated
        const cardNumber = getNestedValue(migrationData, "cardNumber");
        const keyTag = getNestedValue(migrationData, "keyTag");
        migrationSuccessful =
          !isEmptyValue(cardNumber) || !isEmptyValue(keyTag);
        break;

      case "phone_parsing":
        // Phone parsing - check if customer phone was populated
        const phone = getNestedValue(migrationData, "customer.phone");
        migrationSuccessful =
          Array.isArray(phone) &&
          phone.length > 0 &&
          !isEmptyValue(phone[0]?.number);
        break;
    }

    if (migrationSuccessful) {
      hiddenFields.add(fieldKey);
    }
  });

  return hiddenFields;
};

/**
 * Resolve which legacy field slots the card form should hide, for both new and
 * existing cards.
 *
 * Visibility of a "Custom" slot is driven purely by the shop's
 * `cardConfig.cardContents` configuration, never by whether the card happens to
 * hold a value for it — otherwise a card created with its custom fields left
 * blank could never have them filled in later (#1198).
 */
export const resolveHiddenLegacyFields = (
  migrationData: MigrationData,
  cardData: PatchCardDto | undefined,
  shopConfig: Shop | undefined
): Set<CardFieldKeys> => {
  const cardContents = shopConfig?.cardConfig?.cardContents;

  if (!cardContents) {
    return new Set<CardFieldKeys>(LEGACY_CARD_FIELD_KEYS);
  }

  // Creating a card: nothing has been migrated yet, so only Custom slots apply.
  if (!cardData) {
    return new Set<CardFieldKeys>(
      LEGACY_CARD_FIELD_KEYS.filter((key) => cardContents[key] !== "Custom")
    );
  }

  return determineHiddenLegacyFields(migrationData, cardData, shopConfig);
};
